/** Mirrors para-node `src/constants/partner-invoice.constants.ts`. */
export enum PartnerInvoiceStatus {
  Created = 'created',
  Pending = 'pending',
  Canceled = 'canceled',
  Funded = 'funded',
  RepaymentPending = 'repayment_pending',
  Repaid = 'repaid',
}

export enum PartnerInvoiceType {
  Wire = 'wire',
  Loan = 'loan',
  // The admin list endpoint filters these out server-side, so it never arrives on this screen.
  // Kept for completeness because the wire type is shared with other surfaces.
  Historical = 'historical',
}

export enum PartnerInvoiceExternalStatus {
  New = 'NEW',
  Accepted = 'ACCEPTED',
  Declined = 'DECLINED',
  PartiallyPaid = 'PARTIALLY_PAID',
  PaidInFull = 'PAID_IN_FULL',
  PaymentScheduled = 'PAYMENT_SCHEDULED',
}

export enum PartnerInvoiceFundingStatus {
  Pending = 'PENDING',
  Processing = 'PROCESSING',
  Processed = 'PROCESSED',
  Succeeded = 'SUCCEEDED',
  Failed = 'FAILED',
  Corrected = 'CORRECTED',
  Canceled = 'CANCELED',
  Error = 'ERROR',
}

/**
 * Same member set as `PartnerInvoiceFundingStatus` today, but a distinct enum in para-node
 * (`OatfiPaymentStatusEnum` in `src/constants/oatfi.constants.ts`) describing a different
 * column. Kept separate rather than aliased — the two can diverge, and a shared alias would
 * silently mistype `paymentStatus` the day one of them gains a member.
 */
export enum OatfiPaymentStatus {
  Pending = 'PENDING',
  Processing = 'PROCESSING',
  Processed = 'PROCESSED',
  Succeeded = 'SUCCEEDED',
  Failed = 'FAILED',
  Corrected = 'CORRECTED',
  Canceled = 'CANCELED',
  Error = 'ERROR',
}

/**
 * The seven money actions. The value is an internal key only — it is never concatenated into a
 * URL. `INVOICE_ACTION_ENDPOINTS` maps each verb to its `Endpoints` member.
 */
export enum InvoiceActionVerb {
  Cancel = 'cancel',
  MarkFunded = 'markFunded',
  MarkFundedNoCheck = 'markFundedNoCheck',
  ProcessPayment = 'processPayment',
  MarkRepaid = 'markRepaid',
  RepayFees = 'repayFees',
  RepayDueInterest = 'repayDueInterest',
}

/**
 * Derived client-side from the nested partner invoices — the composed-invoice DTO carries no
 * status of its own. See `deriveComposedInvoiceStatus`.
 */
export enum ComposedInvoiceStatus {
  Canceled = 'canceled',
  FundingFailed = 'fundingFailed',
  FundedAndPaid = 'fundedAndPaid',
  Repaying = 'repaying',
  AwaitingFunding = 'awaitingFunding',
}

export enum ComposedInvoicesTableFields {
  invoice = 'invoice',
  type = 'type',
  status = 'status',
  amount = 'amount',
  lateFee = 'lateFee',
  generated = 'generated',
  paymentRelease = 'paymentRelease',
  actions = 'actions',
}

/** Declaration order is column order; the first ten are visible by default. */
export enum PartnerInvoicesTableFields {
  invoice = 'invoice',
  status = 'status',
  type = 'type',
  amount = 'amount',
  paidAmount = 'paidAmount',
  invoiceDate = 'invoiceDate',
  dueDate = 'dueDate',
  earningsCount = 'earningsCount',
  paymentRelease = 'paymentRelease',
  actions = 'actions',
  externalStatus = 'externalStatus',
  loanFundingStatus = 'loanFundingStatus',
  loanFundingErrorMessage = 'loanFundingErrorMessage',
  paymentStatus = 'paymentStatus',
  paymentErrorMessage = 'paymentErrorMessage',
  paymentDate = 'paymentDate',
  payInvoiceOnDate = 'payInvoiceOnDate',
  loanId = 'loanId',
  loanDueDate = 'loanDueDate',
  loanTotalAmount = 'loanTotalAmount',
  loanBaseFeeAmount = 'loanBaseFeeAmount',
  loanTransferFee = 'loanTransferFee',
  loanTransferRate = 'loanTransferRate',
  wireTransferFee = 'wireTransferFee',
  wireTransferRate = 'wireTransferRate',
  gigsafeTotalFeeAmount = 'gigsafeTotalFeeAmount',
  invoiceApprovedByPayor = 'invoiceApprovedByPayor',
  payorExternalId = 'payorExternalId',
  payeeExternalId = 'payeeExternalId',
  paymentId = 'paymentId',
  description = 'description',
}

/**
 * The state of an invoice's scheduled driver-payment hold (ENG-1253). Absent or `null` when the
 * invoice has none.
 *
 * - `Scheduled`  — waiting for its moment; the only state that can be rescheduled or cancelled
 * - `Processing` — the task fired and claimed the release; transient, and the server answers 409
 *                  to a reschedule or cancel. A stuck one is cleared by hand — there is no endpoint
 * - `Released`   — handed to payment processing. It does NOT mean the payments have settled
 * - `Failed`     — the release threw; ops were alerted and nothing retries automatically
 */
export enum PaymentReleaseStatus {
  Scheduled = 'scheduled',
  Processing = 'processing',
  Released = 'released',
  Failed = 'failed',
}

/** What the admin can do to an invoice's hold from its row menu. */
export enum PaymentReleaseActionKind {
  Schedule = 'schedule',
  Reschedule = 'reschedule',
  Cancel = 'cancel',
}

/**
 * Every way a payment-release write can fail. The named members' values are the backend's error
 * NAMES, matched against the body's `error` field — never against `message`, which may change.
 * The other four are classified by HTTP status (or, for `Unknown`, by nothing at all).
 */
export enum PaymentReleaseErrorKind {
  Validation = 'validation',
  Auth = 'auth',
  Permission = 'permission',
  InvoiceNotFound = 'InvoiceNotFoundForReleaseError',
  InvalidReleaseDate = 'InvalidReleaseDateError',
  ReleaseInProgress = 'ReleaseInProgressError',
  AutoReleaseOnFundingEnabled = 'AutoReleaseOnFundingEnabledError',
  NoPaymentsAwaitingRelease = 'NoPaymentsAwaitingReleaseError',
  NoScheduledRelease = 'NoScheduledReleaseError',
  Unknown = 'unknown',
}

/** Why a picked release time cannot be sent, checked client-side before the round trip. */
export enum PaymentReleaseDateProblem {
  Invalid = 'invalid',
  Past = 'past',
  TooFar = 'tooFar',
}
